import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { IconClose, IconLoader, IconLock, IconMicrophone, IconStop } from './icons';

/** Chave única de gravação do Assistente: gravação interrompida é recuperável em qualquer assunto. */
export const ASSISTANT_RECORDING_KEY = 'assistente';

function mmss(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * ECO.6 (25/09/2026) — voz no compositor do Assistente (entrada `/`). Mesmo gravador (`useAudioRecorder`: pedaços
 * de 2,5 s no IndexedDB, recuperação após recarga) e mesmo proxy de transcrição (`/api/transcribe` → Audio
 * Transcriber com `source=record`) dos terminais e do chat — nenhum pipeline novo. O texto transcrito ENTRA NO
 * CAMPO para o dono revisar; nada é enviado sem ele apertar enviar.
 *
 * Devolve um fragmento para a grade do `.topic-composer`: a linha de estado (só com algo a dizer, largura toda) e
 * o botão (coluna própria, alvo de 44 px).
 */
export function AssistantVoiceInput({ onTranscript, disabled }: { onTranscript: (text: string) => void; disabled?: boolean }): JSX.Element {
  const [transcribeEnabled, setTranscribeEnabled] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const { recording, transcribing, notice, audioLevel, recoverable, background, start, stop, cancel, recover, discardRecoverable, clearNotice } =
    useAudioRecorder(api.transcribeForTerminal, ASSISTANT_RECORDING_KEY, undefined, { onBackgroundTranscript: onTranscript });

  useEffect(() => {
    let alive = true;
    // Só para saber se o servidor tem transcrição configurada; falha de leitura não esconde o microfone
    // (o próprio proxy devolve o motivo se não houver serviço).
    Promise.resolve()
      .then(() => api.transcribeInfo())
      .then((s) => { if (alive) setTranscribeEnabled(s.enabled); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!recording) { setElapsed(0); return; }
    const startedAt = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 500);
    return () => clearInterval(timer);
  }, [recording]);

  const onClick = async (): Promise<void> => {
    if (recording) {
      const text = await stop();
      if (text) onTranscript(text);
      return;
    }
    clearNotice();
    await start();
  };

  // ECO4.8-S — onda ao vivo: os últimos ~6 s de volume (uma barra a cada 150 ms), como no Gravador do Google.
  const [wave, setWave] = useState<number[]>([]);
  useEffect(() => {
    if (!recording) { setWave([]); return; }
    const t = setInterval(() => setWave((w) => [...w.slice(-39), Math.max(0.06, Math.min(1, audioLevelRef.current))]), 150);
    return () => clearInterval(t);
  }, [recording]);
  const audioLevelRef = useRef(0);
  audioLevelRef.current = Number.isFinite(audioLevel) ? audioLevel : 0;

  const onRecover = async (): Promise<void> => {
    const text = await recover();
    if (text) onTranscript(text);
  };

  const title = !transcribeEnabled
    ? 'transcrição indisponível (serviço não configurado no servidor)'
    : transcribing
      ? 'transcrevendo…'
      : recording
        ? 'parar e transcrever'
        : 'falar em vez de digitar';
  const state = transcribing ? 'transcribing' : recording ? 'recording' : 'idle';

  return (
    <>
      {recording ? (
        <div className={`assistant-voice-panel${background ? ' is-background' : ''}`} role="status" aria-live="polite" data-testid="assistant-voice-status">
          <div className="assistant-voice-top">
            <span className="assistant-voice-dot" aria-hidden="true" />
            <span className="assistant-voice-label">gravando</span>
            {background ? (
              <span className="assistant-voice-bg" data-testid="assistant-voice-background"><IconLock />pode bloquear a tela</span>
            ) : <span className="assistant-voice-bg is-web">mantenha esta tela aberta</span>}
          </div>
          <div className="assistant-voice-timer" data-testid="assistant-voice-timer">{mmss(elapsed)}</div>
          <div className="assistant-voice-wave" aria-hidden="true">
            {Array.from({ length: 40 }, (_, i) => {
              const v = wave[wave.length - 40 + i] ?? 0;
              return <span key={i} style={{ height: `${Math.round(8 + v * 92)}%`, opacity: v ? 1 : 0.25 }} />;
            })}
          </div>
          <div className="assistant-voice-actions">
            <button type="button" className="assistant-voice-cancel" onClick={() => void cancel()} data-testid="assistant-voice-cancel">
              <IconClose />Cancelar
            </button>
            <button type="button" className="assistant-voice-done" onClick={() => void onClick()} data-testid="assistant-voice-done">
              <IconStop />Parar e transcrever
            </button>
          </div>
        </div>
      ) : notice ? (
        <div className="assistant-voice-status" role="status" data-testid="assistant-voice-status">
          {transcribing ? <IconLoader /> : null}
          <span>{notice}</span>
        </div>
      ) : null}
      {recoverable && !recording && !transcribing ? (
        <div className="assistant-voice-recover" data-testid="assistant-voice-recover">
          <span>{recoverable.reason === 'failed' ? 'a transcrição falhou — o áudio foi preservado' : recoverable.reason === 'device' ? 'há uma gravação guardada no celular que ainda não virou texto' : 'há um áudio de antes da recarga que não foi transcrito'}</span>
          <button type="button" onClick={() => void onRecover()}>{recoverable.reason === 'failed' ? 'tentar novamente' : recoverable.reason === 'device' ? 'transcrever agora' : 'recuperar áudio'}</button>
          <button type="button" className="assistant-voice-discard" onClick={() => void discardRecoverable()} aria-label="descartar o áudio recuperável">descartar</button>
        </div>
      ) : null}
      <button
        type="button"
        className={`assistant-voice-btn is-${state}`}
        title={title}
        aria-label={title}
        aria-pressed={recording}
        disabled={disabled || !transcribeEnabled || transcribing}
        onClick={() => void onClick()}
        data-testid="assistant-voice-btn"
        data-audio-state={state}
      >
        {transcribing ? <IconLoader /> : recording ? <IconStop /> : <IconMicrophone />}
      </button>
    </>
  );
}
